import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { recommendations } from '../../data/recommendations';

const Recommendations = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  if (recommendations.length === 0) {
    return null; // Don't show section if no recommendations
  }

  const goToNext = () => {
    setDirection(1);
    setIsExpanded(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % recommendations.length);
  };

  const goToPrevious = () => {
    setDirection(-1);
    setIsExpanded(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? recommendations.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setIsExpanded(false);
    setCurrentIndex(index);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const currentRec = recommendations[currentIndex];
  const shouldTruncate = currentRec.text.length > 300;

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  return (
    <section id="recommendations" className="section-padding bg-bg-darker">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-6 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-2 md:mb-4">
            Professional <span className="gradient-text">Recommendations</span>
          </h2>
          <p className="text-sm md:text-lg text-text-secondary max-w-2xl mx-auto">
            What colleagues and collaborators have to say about working with me
          </p>
        </div>

        {/* Swipeable Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Carousel Container */}
          <div className="relative overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(_e, { offset, velocity }) => {
                  const swipe = Math.abs(offset.x) * velocity.x;

                  if (swipe < -10000) {
                    goToNext();
                  } else if (swipe > 10000) {
                    goToPrevious();
                  }
                }}
                className="w-full"
              >
                <div className="glass p-4 md:p-8 rounded-xl border-quantum-purple/50 shadow-xl hover:shadow-quantum-purple/20 transition-all duration-300">
                  {/* Quote Icon */}
                  <div className="mb-3 md:mb-4">
                    <svg
                      className="w-8 h-8 md:w-10 md:h-10 text-quantum-purple opacity-30"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>

                  {/* Recommendation Text */}
                  <div className="mb-4 md:mb-6">
                    <p
                      className={`text-text-secondary text-xs md:text-base leading-relaxed ${
                        !isExpanded && shouldTruncate ? 'line-clamp-4' : ''
                      }`}
                    >
                      {currentRec.text}
                    </p>
                    {shouldTruncate && (
                      <button
                        onClick={toggleExpand}
                        className="text-quantum-purple text-xs md:text-sm mt-2 md:mt-3 hover:text-quantum-blue transition-colors font-medium"
                      >
                        {isExpanded ? 'Show less' : 'Read more...'}
                      </button>
                    )}
                  </div>

                  {/* Author Info */}
                  <div className="border-t border-glass-border pt-3 md:pt-4">
                    <p className="text-base md:text-lg font-heading font-bold gradient-text mb-0.5 md:mb-1">
                      {currentRec.name}
                    </p>
                    <p className="text-xs md:text-sm text-text-secondary mb-0.5 md:mb-1">
                      {currentRec.title}
                    </p>
                    <p className="text-xs md:text-sm text-quantum-blue mb-0.5 md:mb-1">
                      {currentRec.company}
                    </p>
                    <p className="text-[10px] md:text-xs text-text-secondary italic">
                      {currentRec.relationship}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows - Hidden on mobile, use swipe instead */}
            <button
              onClick={goToPrevious}
              className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 p-3 rounded-full glass hover:bg-quantum-purple/20 transition-all duration-300 group z-10"
              aria-label="Previous recommendation"
            >
              <svg
                className="w-6 h-6 text-quantum-purple group-hover:text-quantum-blue transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={goToNext}
              className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 p-3 rounded-full glass hover:bg-quantum-purple/20 transition-all duration-300 group z-10"
              aria-label="Next recommendation"
            >
              <svg
                className="w-6 h-6 text-quantum-purple group-hover:text-quantum-blue transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex flex-col items-center gap-2 mt-6 md:mt-8">
            <div className="flex justify-center gap-2 md:gap-3">
              {recommendations.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'w-8 md:w-12 h-2 md:h-3 bg-gradient-to-r from-quantum-purple to-quantum-blue'
                      : 'w-2 md:w-3 h-2 md:h-3 bg-glass-border hover:bg-quantum-purple/50'
                  }`}
                  aria-label={`Go to recommendation ${index + 1}`}
                />
              ))}
            </div>
            <p className="md:hidden text-[10px] text-text-secondary/50">Swipe to navigate</p>
          </div>
        </div>

        {/* LinkedIn CTA */}
        <div className="text-center mt-8 md:mt-12">
          <a
            href="https://www.linkedin.com/in/danielhostrow/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            View All on LinkedIn
          </a>
        </div>
      </div>
    </section>
  );
};

export default Recommendations;
